import { useCallback, useEffect, useState } from "react";

// Replace with env
const RECAPTCHA_KEY = "6Lf2XVghAAAAALc7yDQ6c_TSWwAp0feOju0mXfVF";

// The Google script assigns this once it has loaded, so it can be missing.
interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (site_key: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

// Returns the tag already in the head when there is one, so a second caller does
// not re-fetch the script.
const addRecaptchaScript = () => {
  const existing = document.getElementById("g-recaptcha-script");
  if (existing) return existing;
  const script = document.createElement("script");
  script.setAttribute("src", `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_KEY}`);
  script.setAttribute("id", "g-recaptcha-script");
  document.head.appendChild(script);
  return script;
};

const useRecaptcha = () => {
  const [loading, setLoading] = useState(true);

  const _setBadgeVisibility = useCallback((show_badge: boolean) => {
    const badges = document.querySelectorAll<HTMLElement>(".grecaptcha-badge");
    for (const badge of badges) {
      badge.style.visibility = show_badge ? "visible" : "hidden";
    }
  }, []);

  useEffect(() => {
    const script = addRecaptchaScript();
    const release = () => setLoading(false);
    script.addEventListener("load", release, { once: true });
    // A failed load still has to release the page, or the spinner never clears.
    // verify() reports the missing grecaptcha on its own.
    script.addEventListener("error", release, { once: true });
    return () => {
      script.removeEventListener("load", release);
      script.removeEventListener("error", release);
      script.remove();
      _setBadgeVisibility(false);
    };
  }, [_setBadgeVisibility]);

  const show = useCallback(() => {
    addRecaptchaScript();
    _setBadgeVisibility(true);
  }, [_setBadgeVisibility]);

  const hide = useCallback(() => {
    document.getElementById("g-recaptcha-script")?.remove();
    _setBadgeVisibility(false);
  }, [_setBadgeVisibility]);

  // Resolves with a fresh token. `loading` covers the round trip to Google and
  // nothing past it, so the caller chains its own work onto the returned promise
  // rather than handing in a continuation.
  const verify = useCallback((action = "login") => {
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) {
      return Promise.reject(new Error("ReCaptcha not initialized"));
    }
    setLoading(true);
    return new Promise<string>((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(RECAPTCHA_KEY, { action }).then(resolve, reject);
      });
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  return { show, hide, verify, loading };
};

export default useRecaptcha;
